import React, { useState, useEffect } from 'react';
import QuizList from './components/QuizList';
import Quiz from './components/Quiz';
import QuizForm from './components/CreateQuiz/QuizForm';
import EditQuizForm from './components/EditQuiz/EditQuizForm';
import QuizSettings from './components/QuizSettings';
import MyPage from './components/MyPage/MyPage';
import SharedQuiz from './components/SharedQuiz/SharedQuiz';
import PublicQuizList from './components/PublicQuizList/PublicQuizList';
import LandingPage from './components/LandingPage/LandingPage';
import AuthProvider, { useAuth } from './components/Auth/AuthProvider';
import AuthPage from './components/Auth/AuthPage';
import { createQuiz, deleteQuiz } from './firebase';
import { quizService } from './services/quizService';
import ErrorBoundary from './utils/errorBoundary';
import logger from './utils/logger';
import './utils/firestoreDebug'; // Auto-run debug
import './App.css';
import HamburgerMenu from './components/Common/HamburgerMenu';
import ScrollToBottomButton from './components/Common/ScrollToBottomButton';


function AppContent() {
  const [currentView, setCurrentView] = useState('quiz-list');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [editingQuizId, setEditingQuizId] = useState(null);
  const [quizSettingsData, setQuizSettingsData] = useState(null);
  const [quizSettings, setQuizSettings] = useState(null);
  const { user, loading } = useAuth();

  // Check if this is a shared quiz URL, public quiz list, or landing page
  const [isSharedQuiz, setIsSharedQuiz] = useState(false);
  const [sharedQuizParams, setSharedQuizParams] = useState(null);
  const [isPublicQuizList, setIsPublicQuizList] = useState(false);
  const [isLandingPage, setIsLandingPage] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const sharedQuizMatch = path.match(/^\/shared-quiz\/([^\/]+)\/([^\/]+)$/);
    
    if (sharedQuizMatch) {
      setIsSharedQuiz(true);
      setSharedQuizParams({
        userId: sharedQuizMatch[1],
        quizId: sharedQuizMatch[2]
      });
    } else if (path === '/public') {
      setIsPublicQuizList(true);
    } else if (path === '/landing') {
      setIsLandingPage(true);
    }
  }, [user]);

  useEffect(() => {
    logger.componentMount('App');
    logger.info('Quizenie App started', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    }, 'APP');

    // Global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      logger.error('Unhandled promise rejection', {
        reason: event.reason,
        promise: event.promise
      }, 'APP');
    };

    // Global error handler for JavaScript errors
    const handleError = (event) => {
      logger.error('Global JavaScript error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      }, 'APP');
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
      logger.componentUnmount('App');
    };
  }, []);

  const handleSelectQuiz = async (quizId, userId) => {
    try {
      logger.userAction('navigate_to_quiz_settings', { quizId, userId });
      
      // Load quiz data and questions for settings
      const [quizData, questionsData] = await Promise.all([
        quizService.getQuiz(userId, quizId),
        quizService.getQuizQuestions(userId, quizId)
      ]);
      
      setQuizSettingsData({
        quiz: quizData,
        questions: questionsData,
        quizId,
        userId
      });
      setCurrentView('quiz-settings');
    } catch (error) {
      logger.error('Failed to load quiz for settings', { error: error.message, quizId, userId });
      alert('クイズの読み込みに失敗しました');
    }
  };
  
  const handleStartQuizWithSettings = (settings) => {
    logger.userAction('start_quiz_with_settings', { 
      quizId: quizSettingsData.quizId, 
      userId: quizSettingsData.userId,
      settings
    });
    
    setSelectedQuiz({ 
      quizId: quizSettingsData.quizId, 
      userId: quizSettingsData.userId 
    });
    setQuizSettings(settings);
    setCurrentView('quiz');
  };
  
  const handleBackFromQuizSettings = () => {
    setCurrentView('quiz-list');
    setQuizSettingsData(null);
  };

  const handleBackToQuizList = () => {
    logger.userAction('navigate_to_quiz_list');
    setCurrentView('quiz-list');
    setSelectedQuiz(null);
    setQuizSettings(null);
  };

  const handleRestartWithSettings = () => {
    logger.userAction('restart_quiz_with_settings', { 
      quizId: selectedQuiz.quizId, 
      userId: selectedQuiz.userId 
    });
    setCurrentView('quiz-settings');
    setSelectedQuiz(null);
    setQuizSettings(null);
  };

  const handleCreateQuiz = () => {
    logger.userAction('navigate_to_create_quiz');
    setCurrentView('create-quiz');
  };

  const handleSaveQuiz = async (quizData) => {
    try {
      const quizId = await createQuiz(user.uid, quizData);
      logger.userAction('quiz_created', { quizId, title: quizData.title });
      alert('クイズが作成されました！');
      setCurrentView('quiz-list');
    } catch (error) {
      logger.error('Failed to create quiz', { error: error.message });
      throw error;
    }
  };

  const handleCancelCreateQuiz = () => {
    setCurrentView('quiz-list');
  };

  const handleEditQuiz = (quizId) => {
    logger.userAction('navigate_to_edit_quiz', { quizId });
    setEditingQuizId(quizId);
    setCurrentView('edit-quiz');
  };

  const handleSaveEditQuiz = () => {
    setCurrentView('quiz-list');
    setEditingQuizId(null);
  };

  const handleCancelEditQuiz = () => {
    setCurrentView('quiz-list');
    setEditingQuizId(null);
  };

  const handleDeleteQuiz = async (quizId, quizTitle) => {
    try {
      await deleteQuiz(user.uid, quizId);
      logger.userAction('quiz_deleted', { quizId, quizTitle });
      alert('クイズが削除されました');
    } catch (error) {
      logger.error('Failed to delete quiz', { error: error.message, quizId, quizTitle });
      throw error;
    }
  };

  const handleGoToMyPage = () => {
    logger.userAction('navigate_to_my_page');
    setCurrentView('my-page');
  };

  const handleBackFromMyPage = () => {
    setCurrentView('quiz-list');
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading-spinner">読み込み中...</div>
      </div>
    );
  }

  // Show landing page if not authenticated and no specific action requested
  if (isLandingPage) {
    return (
      <ErrorBoundary>
        <LandingPage />
        <ScrollToBottomButton />
      </ErrorBoundary>
    );
  }

  // Show shared quiz if it's a shared quiz URL
  if (isSharedQuiz && sharedQuizParams) {
    return (
      <>
        <SharedQuiz userId={sharedQuizParams.userId} quizId={sharedQuizParams.quizId} />
        <ScrollToBottomButton />
      </>
    );
  }

  // Show public quiz list if it's /public path
  if (isPublicQuizList) {
    return (
      <ErrorBoundary>
        <div className="App">
        <header className="App-header">
          <div className="header-left">
            <HamburgerMenu onCreateQuiz={handleCreateQuiz} onGoToMyPage={handleGoToMyPage} />
          </div>
          <div className="header-title">
            <img src="/apple-touch-icon.png" alt="Quizenie Icon" className="app-icon" />
            <h1>Quizenie</h1>
          </div>
          <p>クイズで学習しよう！</p>
        </header>
          <main className="App-main">
            <ErrorBoundary>
              <PublicQuizList onSelectQuiz={handleSelectQuiz} />
            </ErrorBoundary>
          </main>
          <footer className="App-footer">
            <p>&copy; 2025 Quizenie. All rights reserved.</p>
          </footer>
        </div>
        <ScrollToBottomButton />
      </ErrorBoundary>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <header className="App-header">
          <div className="header-left">
            <HamburgerMenu onCreateQuiz={handleCreateQuiz} onGoToMyPage={handleGoToMyPage} />
          </div>
          <div className="header-title">
            <img src="/apple-touch-icon.png" alt="Quizenie Icon" className="app-icon" />
            <h1>Quizenie</h1>
          </div>
          <p>クイズで学習しよう！</p>
        </header>

        <main className="App-main">
          <ErrorBoundary>
            {currentView === 'quiz-list' && (
              <QuizList 
                onSelectQuiz={handleSelectQuiz}
                onCreateQuiz={handleCreateQuiz}
                onEditQuiz={handleEditQuiz}
                onDeleteQuiz={handleDeleteQuiz}
                onBack={null}
              />
            )}
          </ErrorBoundary>
          
          <ErrorBoundary>
            {currentView === 'quiz-settings' && quizSettingsData && (
              <QuizSettings 
                quiz={quizSettingsData.quiz}
                totalQuestions={quizSettingsData.questions.length}
                onStartQuiz={handleStartQuizWithSettings}
                onCancel={handleBackFromQuizSettings}
              />
            )}
          </ErrorBoundary>
          
          <ErrorBoundary>
            {currentView === 'quiz' && selectedQuiz && quizSettings && (
              <Quiz 
                quizId={selectedQuiz.quizId}
                userId={selectedQuiz.userId}
                quizSettings={quizSettings}
                onBack={handleBackToQuizList}
                onRestartWithSettings={handleRestartWithSettings}
              />
            )}
          </ErrorBoundary>

          <ErrorBoundary>
            {currentView === 'create-quiz' && (
              <QuizForm 
                onSave={handleSaveQuiz}
                onCancel={handleCancelCreateQuiz}
              />
            )}
          </ErrorBoundary>

          <ErrorBoundary>
            {currentView === 'edit-quiz' && editingQuizId && (
              <EditQuizForm 
                quizId={editingQuizId}
                onSave={handleSaveEditQuiz}
                onCancel={handleCancelEditQuiz}
              />
            )}
          </ErrorBoundary>

          <ErrorBoundary>
            {currentView === 'my-page' && (
              <MyPage 
                onBack={handleBackFromMyPage}
              />
            )}
          </ErrorBoundary>
        </main>

        <footer className="App-footer">
          <p>&copy; 2025 Quizenie. All rights reserved.</p>
        </footer>
        
        <ScrollToBottomButton />
      </div>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
